// src/utils/subdomain.ts
function extractSubdomain(host, rootDomain) {
  if (!host || !rootDomain) {
    return null;
  }
  const hostWithoutPort = host.split(":")[0] ?? "";
  const rootDomainWithoutPort = rootDomain.split(":")[0] ?? "";
  const normalizedHost = hostWithoutPort.toLowerCase();
  const normalizedRootDomain = rootDomainWithoutPort.toLowerCase();
  if (!normalizedHost.endsWith(normalizedRootDomain)) {
    return null;
  }
  if (normalizedHost === normalizedRootDomain) {
    return null;
  }
  const expectedSuffix = `.${normalizedRootDomain}`;
  if (!normalizedHost.endsWith(expectedSuffix)) {
    return null;
  }
  const subdomain = normalizedHost.slice(0, -expectedSuffix.length);
  if (!subdomain || subdomain.includes(".")) {
    return null;
  }
  return subdomain;
}

// src/utils/format-magnitude.ts
function formatMagnitude(value) {
  if (!Number.isFinite(value)) return "0";
  if (value === 0) return "0";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs >= 1e6) {
    return `${sign}${(abs / 1e6).toFixed(1)}M`;
  }
  if (abs >= 1e3) {
    return `${sign}${(abs / 1e3).toFixed(1)}K`;
  }
  return `${sign}${abs}`;
}

// src/utils/format-duration.ts
var SECOND_MS = 1e3;
var MINUTE_MS = 60 * SECOND_MS;
var HOUR_MS = 60 * MINUTE_MS;
var DAY_MS = 24 * HOUR_MS;
var pad = (n) => String(n).padStart(2, "0");
function formatDurationMs(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "\u2014";
  if (ms < SECOND_MS) return `${Math.floor(ms)} ms`;
  if (ms < MINUTE_MS) return `${(Math.floor(ms / 100) / 10).toFixed(1)} s`;
  if (ms < HOUR_MS) {
    return `${Math.floor(ms / MINUTE_MS)} min ${pad(Math.floor(ms % MINUTE_MS / SECOND_MS))} s`;
  }
  if (ms < DAY_MS) {
    return `${Math.floor(ms / HOUR_MS)} h ${pad(Math.floor(ms % HOUR_MS / MINUTE_MS))} min`;
  }
  return `${Math.floor(ms / DAY_MS)} d ${Math.floor(ms % DAY_MS / HOUR_MS)} h`;
}

// src/utils/time-buckets.ts
var BUCKET_GRAINS = ["minute", "hour", "day", "week", "month"];
var MINUTE_MS2 = 6e4;
var HOUR_MS2 = 60 * MINUTE_MS2;
var DAY_MS2 = 24 * HOUR_MS2;
var WEEK_MS = 7 * DAY_MS2;
var GRAIN_MS = {
  minute: MINUTE_MS2,
  hour: HOUR_MS2,
  day: DAY_MS2,
  week: WEEK_MS
};
function truncateToGrain(instant, grain) {
  const t = instant.getTime();
  switch (grain) {
    case "month":
      return new Date(Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), 1));
    case "week": {
      const day = new Date(t - t % DAY_MS2);
      const sinceMonday = (day.getUTCDay() + 6) % 7;
      return new Date(day.getTime() - sinceMonday * DAY_MS2);
    }
    default:
      return new Date(t - t % GRAIN_MS[grain]);
  }
}
function addGrain(start, grain, count = 1) {
  if (grain === "month") {
    return new Date(
      Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + count, start.getUTCDate())
    );
  }
  return new Date(start.getTime() + count * GRAIN_MS[grain]);
}
function bucketStarts(from, to, grain) {
  const starts = [];
  if (to <= from) return starts;
  for (let start = truncateToGrain(from, grain); start < to; start = addGrain(start, grain)) {
    starts.push(start);
  }
  return starts;
}
var pad2 = (n) => String(n).padStart(2, "0");
function isoWeek(monday) {
  const thursday = new Date(monday.getTime() + 3 * DAY_MS2);
  const year = thursday.getUTCFullYear();
  return { year, week: 1 + Math.floor((thursday.getTime() - Date.UTC(year, 0, 1)) / WEEK_MS) };
}
function bucketLabel(start, grain) {
  const date = `${start.getUTCFullYear()}-${pad2(start.getUTCMonth() + 1)}-${pad2(start.getUTCDate())}`;
  switch (grain) {
    case "minute":
    case "hour":
      return `${date}T${pad2(start.getUTCHours())}:${pad2(start.getUTCMinutes())}Z`;
    case "day":
      return date;
    case "week": {
      const { year, week } = isoWeek(start);
      return `${year}-W${pad2(week)}`;
    }
    case "month":
      return date.slice(0, 7);
  }
}
function bucketCount(from, to, grain) {
  if (to <= from) return 0;
  const first = truncateToGrain(from, grain);
  const last = truncateToGrain(new Date(to.getTime() - 1), grain);
  if (grain === "month") {
    return (last.getUTCFullYear() - first.getUTCFullYear()) * 12 + last.getUTCMonth() - first.getUTCMonth() + 1;
  }
  return (last.getTime() - first.getTime()) / GRAIN_MS[grain] + 1;
}
var COVERAGE_UNIT = {
  minute: { unit: "seconds", ms: 1e3 },
  hour: { unit: "minutes", ms: MINUTE_MS2 },
  day: { unit: "hours", ms: HOUR_MS2 },
  week: { unit: "days", ms: DAY_MS2 },
  month: { unit: "days", ms: DAY_MS2 }
};
function bucketCoverage(start, grain, from, to) {
  const end = addGrain(start, grain);
  const { unit, ms } = COVERAGE_UNIT[grain];
  const overlap = Math.min(end.getTime(), to.getTime()) - Math.max(start.getTime(), from.getTime());
  return {
    covered: Math.max(1, Math.round(overlap / ms)),
    of: Math.round((end.getTime() - start.getTime()) / ms),
    unit
  };
}
function finestGrainWithin(from, to, maxBuckets, coarserThan) {
  const coarser = BUCKET_GRAINS.slice(BUCKET_GRAINS.indexOf(coarserThan) + 1);
  return coarser.find((grain) => bucketCount(from, to, grain) <= maxBuckets) ?? null;
}

// src/utils/http-errors.ts
function responseOf(error) {
  return typeof error === "object" && error !== null ? error.response : void 0;
}
function httpStatus(error) {
  return responseOf(error)?.status;
}
function isRateLimited(error) {
  return httpStatus(error) === 429;
}
function retryAfterMs(error, maxMs) {
  const raw = responseOf(error)?.headers?.["retry-after"];
  if (typeof raw !== "string" || !/^\d+$/.test(raw.trim())) return void 0;
  return Math.min(Number(raw) * 1e3, maxMs);
}
function retryDelayMs(attempt, error, maxRetryAfterMs) {
  return retryAfterMs(error, maxRetryAfterMs) ?? Math.min(2 ** attempt * 1e3, 3e4);
}

// src/utils/index.ts
function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}
function slugify(str) {
  return str.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-|-$/g, "");
}
function formatDate(date, locale = "en-US") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}
function formatDateTime(date, locale = "en-US") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
function isValidDate(date) {
  return date instanceof Date && !isNaN(date.getTime());
}
function omit(obj, keys) {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
}
function pick(obj, keys) {
  const result = {};
  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
async function retry(fn, options = {}) {
  const { maxAttempts = 3, delayMs = 1e3, backoff = true } = options;
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt < maxAttempts) {
        const delay = backoff ? delayMs * attempt : delayMs;
        await sleep(delay);
      }
    }
  }
  throw lastError;
}
async function withTimeout(promise, ms, message) {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error(message)), ms);
      })
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
function generateId(prefix = "") {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 9);
  return prefix ? `${prefix}_${timestamp}${randomPart}` : `${timestamp}${randomPart}`;
}

export { BUCKET_GRAINS, addGrain, bucketCount, bucketCoverage, bucketLabel, bucketStarts, capitalize, extractSubdomain, finestGrainWithin, formatDate, formatDateTime, formatDurationMs, formatMagnitude, generateId, httpStatus, isRateLimited, isValidDate, omit, pick, retry, retryAfterMs, retryDelayMs, sleep, slugify, truncateToGrain, withTimeout };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map